// format.js — display helpers. Nothing here parses or validates; the backend
// owns truth and these only decide how it reads on a small screen.

const RELATIVE = [
  [60, 1, 's'],
  [3600, 60, 'm'],
  [86400, 3600, 'h'],
  [2592000, 86400, 'd'],
];

/** "just now" · "4m ago" · "3d ago" · a date beyond 30 days. */
export function ago(iso) {
  if (!iso) return '—';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  const secs = Math.max(0, (Date.now() - then) / 1000);
  if (secs < 8) return 'just now';
  for (const [limit, div, unit] of RELATIVE) {
    if (secs < limit) return `${Math.floor(secs / div)}${unit} ago`;
  }
  return new Date(then).toLocaleDateString(undefined,
    { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Absolute, with seconds — for audit rows, where "2h ago" is not evidence. */
export function stamp(iso) {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  return new Date(t).toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit',
    minute: '2-digit', second: '2-digit', hour12: false,
  });
}

export function duration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60000);
  return `${m}m ${Math.round((ms % 60000) / 1000)}s`;
}

export function num(n) {
  return typeof n === 'number' ? n.toLocaleString() : '—';
}

export function initials(user) {
  const src = (user?.display_name || user?.email || '?').trim();
  const words = src.split(/[\s@._-]+/).filter(Boolean);
  return ((words[0]?.[0] || '') + (words[1]?.[0] || '')).toUpperCase() || '?';
}

/** Sum of a `{status: count}` map — job/role/state tallies come back this way. */
export function total(counts) {
  return Object.values(counts || {}).reduce((a, b) => a + (b || 0), 0);
}

const JOB_TONE = {
  succeeded: 'ok',
  running: 'info',
  queued: 'info',
  failed: 'danger',
  interrupted: 'warn',
  cancelled: '',
};

export function jobTone(status) { return JOB_TONE[status] || ''; }

/** Human copy for the flags /admin/support/lookup and /admin/users/{id} set.
 *  `tone` drives the pill colour; `hint` is what to actually do about it. */
export const FLAG_COPY = {
  not_provisioned: {
    tone: 'danger',
    label: 'Not provisioned',
    hint: 'No account exists for this email. Provision it in the right org — Mono has no self-signup.',
  },
  org_inactive: {
    tone: 'danger',
    label: 'Org deactivated',
    hint: 'The whole organisation is off. Nobody in it can sign in until it is reactivated.',
  },
  user_inactive: {
    tone: 'danger',
    label: 'User deactivated',
    hint: 'This account is switched off. Reactivate it from the user sheet.',
  },
  never_signed_in: {
    tone: 'warn',
    label: 'Never signed in',
    hint: 'Provisioned but no Google account is bound yet — the binding happens on first sign-in.',
  },
  no_active_session: {
    tone: '',
    label: 'No active session',
    hint: 'Not signed in anywhere right now. Normal outside working hours.',
  },
  recent_job_failures: {
    tone: 'warn',
    label: 'Recent job failures',
    hint: 'Jobs failed in the window below — open Monitor for the error text.',
  },
};
